import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useProviderRFIMetrics,
  useProviderAssessmentMetrics,
  useGetVendorRfis,
  useSponsorRFIMetrics,
  useAssessmentMetrics,
  useProviderDocumentCounts,
} from "./queries";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";

const DEFAULT_TEAM_SIZE = 0;

/**
 * Options for useAccountManagementStats (vendor only).
 * @typedef {Object} UseAccountManagementStatsOptions
 * @property {number} [teamSize] - Override team size (e.g. from usersList.length).
 * @property {number} [providerId] - Provider ID.
 * @property {boolean} [isVendorView] - Whether the view is for a vendor.
 * @property {string} [userType] - The type of user.
 */

/**
 * Hook that computes vendor stats for the Account Management page (VendorAccountManagement).
 * Stats: Team Size, Total Requests / In Progress, Completed VQQ Templates
 *
 * @param {UseAccountManagementStatsOptions} [options] - Optional: teamSize
 * @returns {{ stats, statsDetails, isLoading }}
 */
export function useAccountManagementStats(options = {}) {
  const {
    teamSize: teamSizeOverride,
    teamSummary: teamSummaryData,
    userId,
    userType = "vendor",
    isVendorView = true,
    isSponsorView = false,
    enabled = true,
  } = options;

  const rfisState = useSelector((state) => state.rfisColors);
  const rfisListColorsMeta = useMemo(
    () => rfisState?.rfis || [],
    [rfisState?.rfis],
  );

  // Document counts from DocumentService (single source of truth for totals)
  const { data: providerDocCountsData, isLoading: isLoadingProviderDocCounts } =
    useProviderDocumentCounts({ enabled: isVendorView, providerId: userId });

  // Vendor RFI/Assessment Metrics (still needed for "In Progress" counts)
  const { data: providerRfiMetricsData, isLoading: isLoadingProviderRfiMetrics } =
    useProviderRFIMetrics({ enabled: enabled && isVendorView, providerId: userId });
  const {
    data: providerAssessmentMetricsData,
    isLoading: isLoadingProviderAssessmentMetrics,
  } = useProviderAssessmentMetrics({ enabled: enabled && isVendorView, providerId: userId });
  const {
    data: rfisVendorListData,
    isLoading: rfiVendorIsLoading,
  } = useGetVendorRfis(
    0,
    1000,
    null,
    null,
    null,
    null,
    null,
    null,
    [userId],
    undefined,
    0,
    enabled && isVendorView
  );

  // Sponsor RFI Metrics
  const {
    data: sponsorRfiMetricsData,
    isLoading: sponsorRfiMetricsIsLoading,
  } = useSponsorRFIMetrics({ enabled: enabled && isSponsorView, sponsorId: userId });
  const {
    data: sponsorAssessmentMetricsData,
    isLoading: sponsorAssessmentMetricsIsLoading,
  } = useAssessmentMetrics({ enabled: enabled && isSponsorView, sponsorId: userId });

  // Metrics calculations
  const completedRfisTemplates = useMemo(
    () =>
      Array.from(
        new Set(
          rfisVendorListData?.items
            ?.filter((item) => item.status?.toLowerCase() === "complete")
            ?.map((item) => item.services),
        ),
      ),
    [rfisVendorListData],
  );

  const requestSponsors = useMemo(
    () =>
      Array.from(
        new Set(rfisVendorListData?.items?.map((item) => item.sponsor_id)),
      )?.length || 0,
    [rfisVendorListData],
  );

  const teamSize = useMemo(() => {
    if (typeof teamSizeOverride === "number") return String(teamSizeOverride);
    return String(DEFAULT_TEAM_SIZE);
  }, [teamSizeOverride]);

  const stats = useMemo(() => {
    // Vendor view: use DocumentService for totals (same source as listing table)
    // Sponsor view: use RFI/Assessment metrics (unchanged)
    const totalRequests = isVendorView
      ? (providerDocCountsData?.[0]?.total_count ?? 0)
      : Number(sponsorRfiMetricsData?.total_count || 0) +
        Number(sponsorAssessmentMetricsData?.total_vqa_requests || 0);

    // In Progress still comes from RFI/Assessment metrics (DocumentService doesn't track request status)
    const inProgress =
      Number(sponsorRfiMetricsData?.in_progress_count || providerRfiMetricsData?.in_progress_count || 0) +
      Number(
        sponsorAssessmentMetricsData?.total_inprogress_vqa_requests || providerAssessmentMetricsData?.total_inprogress_vqa_requests || 0,
      );

    // Return stats based on view type
    return isVendorView ? [
      { name: "Team Size", value: teamSize },
      {
        name: "Total Requests / In Progress",
        value: totalRequests,
        subValue: inProgress,
        subValueColor: "text-green-600",
        separator: "/",
      },
      {
        name: "Completed VQQ Templates",
        value: completedRfisTemplates.length || 0,
      },
    ] : [
      { name: "Team Size", value: teamSize },
      { name: "Total Requests", value: totalRequests ?? 0 },
      { name: "Active Requests", value: inProgress ?? 0 },
      // { name: "Platform Credits", value: 35 ?? 0 }, // TODO: get platform credits from API
    ];
  }, [
    teamSize,
    providerDocCountsData,
    providerRfiMetricsData,
    providerAssessmentMetricsData,
    completedRfisTemplates.length,
    sponsorRfiMetricsData,
    sponsorAssessmentMetricsData,
    isVendorView,
  ]);

  // Return stats details based on view type (only vendors for now)
  const statsDetails = useMemo(
    () => isVendorView ? ({
      "Team Size": [
        { label: "Admins", value: teamSummaryData?.admins_count ?? 0 },
        { label: "VQQ Managers - Approvers", value: teamSummaryData?.rfi_managers_approvers_count ?? 0 },
        { label: "VQQ Managers - Users", value: teamSummaryData?.rfi_managers_users_count ?? 0 },
        { label: "VQA Managers", value: teamSummaryData?.vqa_managers_count ?? 0 },
      ],
      "Total Requests / In Progress": [
        { label: "Sponsors", value: requestSponsors },
        {
          label: "VQQ Requests",
          value: Number(providerDocCountsData?.[0]?.rfi_count || 0),
          link: userType === "vendor" ? "/Provider/RFI/Requests" : "/Backend/Rfis",
        },
        {
          label: "Assessment Requests",
          value: Number(providerDocCountsData?.[0]?.assessment_count || 0),
          link: userType === "vendor" ? "/Provider/Assessment/History" : "/Backend/Assessment/Request/History",
        },
      ],
      "Completed VQQ Templates": completedRfisTemplates
        .slice(0, 9)
        .map((rfi) => {
          const meta = findRfiColorMeta(rfi, rfisListColorsMeta);
          return {
            label: rfi,
            badge: true,
            badgeColor: meta
              ? [meta.bg, meta.text, meta.border].filter(Boolean).join(" ")
              : "bg-gray-100 text-gray-700",
          };
        }),
    }) : {},
    [
      requestSponsors,
      providerDocCountsData,
      completedRfisTemplates,
      rfisListColorsMeta,
      userType,
      isVendorView
    ],
  );

  const isLoading =
    isLoadingProviderDocCounts ||
    isLoadingProviderRfiMetrics ||
    isLoadingProviderAssessmentMetrics ||
    rfiVendorIsLoading ||
    sponsorRfiMetricsIsLoading ||
    sponsorAssessmentMetricsIsLoading;

  return { stats, statsDetails, isLoading };
}
